import type { ClaimablesData, SpinningWheelResult } from "@cede/types";
import type { RewardEpochDetails } from "@/hooks";
import Decimal from "decimal.js";

export type RewardStatus = "pending" | "claimable" | "claimed";

export interface EnrichedSpinResult extends SpinningWheelResult {
  status: RewardStatus;
  cooldownEnd?: Date;
  claimableReward?: RewardEpochDetails;
  // Raw claimable data with calldata and merkle proof for individual claiming
  claimableData?: ClaimablesData;
}

/**
 * Epoch group containing multiple spinning wheel results
 * All results in the same epoch share the same status and claim button
 */
export interface EpochGroup {
  epochId: number;
  results: EnrichedSpinResult[];
  status: RewardStatus;
  cooldownEnd?: Date;
  claimableData?: ClaimablesData;
  totalRewards: { [symbol: string]: number };
}

/**
 * Calculate estimated cooldown end date for a spin result
 * Logic: Next midnight after spin + 24 hours cooldown period
 */
export function getEstimatedCooldownEnd(createdAt: Date): Date {
  // Find next midnight after the spin
  const nextMidnight = new Date(createdAt);
  nextMidnight.setDate(nextMidnight.getDate() + 1);
  nextMidnight.setHours(0, 0, 0, 0);

  // Add 24-hour cooldown period
  const estimatedEnd = new Date(nextMidnight);
  estimatedEnd.setHours(estimatedEnd.getHours() + 24);

  return estimatedEnd;
}

/**
 * Determine the status of a reward based on database fields
 * Three states: pending (waiting for cooldown to end), claimable (ready to claim), claimed (already claimed)
 */
export function determineRewardStatus(
  spinResult: SpinningWheelResult,
  claimableReward?: RewardEpochDetails,
  claimableSoonReward?: RewardEpochDetails,
): RewardStatus {
  const now = new Date();

  // Priority 1: Check if already claimed (database field)
  if (spinResult.claimedAt) {
    return "claimed";
  }

  // Priority 2: Check cooldownEnd from database
  if (spinResult.cooldownEnd) {
    const cooldownEnd = new Date(spinResult.cooldownEnd);

    // Cooldown has passed - reward is claimable
    if (cooldownEnd <= now) {
      return "claimable";
    }

    // Still in cooldown period - show as pending
    return "pending";
  }

  // Priority 3: Check hub-rewards API data
  if (claimableReward) {
    return "claimable";
  }

  if (claimableSoonReward?.cooldownEnd) {
    const cooldownEnd = new Date(claimableSoonReward.cooldownEnd);

    if (cooldownEnd <= now) {
      return "claimable";
    }

    // Still in cooldown - show as pending
    return "pending";
  }

  // Priority 4: No cooldownEnd means pending distribution (waiting for midnight cron)
  return "pending";
}

/**
 * Merge spinning wheel history with claimable rewards data
 */
export function mergeHistoryWithClaimData(
  historyResults: SpinningWheelResult[],
  claimableRewards: RewardEpochDetails[],
  claimableSoonRewards: RewardEpochDetails[],
  claimablesData?: ClaimablesData[],
): EnrichedSpinResult[] {
  return historyResults.map((result) => {
    // Match by epochId (authoritative and consistent with claimableData matching)
    const claimableReward = claimableRewards.find((reward) => {
      return reward.epochId === result.epochId;
    });

    const claimableSoonReward = claimableSoonRewards.find((reward) => {
      return reward.epochId === result.epochId;
    });

    // Find the matching ClaimablesData (with calldata and merkle proof)
    // Match by epochId (authoritative - all rewards here are spinning wheel)
    const claimableData = claimablesData?.find((claimable) => {
      // Verify it's a spinning wheel reward (should always be true in this component)
      if (claimable.campaignType !== "SPINNING_WHEEL") {
        return false;
      }

      // Match by epochId from claimableData (from calldata.epochId)
      return claimable.calldata.epochId === result.epochId;
    });

    const status = determineRewardStatus(result, claimableReward, claimableSoonReward);

    // Determine cooldown end date
    let cooldownEnd: Date | undefined;

    // Priority 1: Use database cooldownEnd if available
    if (result.cooldownEnd) {
      cooldownEnd = new Date(result.cooldownEnd);
    }
    // Priority 2: Use hub-rewards API cooldown for validation
    else if (claimableSoonReward?.cooldownEnd) {
      cooldownEnd = new Date(claimableSoonReward.cooldownEnd);
    }
    // Priority 3: Estimate cooldown for pending rewards
    else if (status === "pending") {
      cooldownEnd = getEstimatedCooldownEnd(new Date(result.createdAt));
    }

    return {
      ...result,
      status,
      cooldownEnd,
      claimableReward,
      claimableData,
    };
  });
}

/**
 * Group enriched spin results by epochId
 * All results in the same epoch share the same status and claim button
 */
export function groupResultsByEpoch(enrichedResults: EnrichedSpinResult[]): EpochGroup[] {
  // Group results by epochId
  const groupsMap = new Map<number, EnrichedSpinResult[]>();

  for (const result of enrichedResults) {
    const epochId = result.epochId;
    // Skip results without an epochId (shouldn't happen in practice)
    if (epochId === null) continue;

    const group = groupsMap.get(epochId);
    if (group) {
      group.push(result);
    } else {
      groupsMap.set(epochId, [result]);
    }
  }

  // Convert to EpochGroup array
  const epochGroups: EpochGroup[] = [];

  for (const [epochId, results] of groupsMap.entries()) {
    // Calculate total rewards per token symbol using Decimal.js for precision
    const totalRewards: { [symbol: string]: number } = {};
    for (const result of results) {
      const symbol = result.rewardSymbol;
      // Use Decimal.js to avoid floating point precision loss
      const currentTotal = totalRewards[symbol] || 0;
      const newTotal = new Decimal(currentTotal).plus(result.rewardAmount);
      totalRewards[symbol] = newTotal.toNumber();
    }

    // Determine unified status for the epoch (priority: claimed > claimable > pending)
    let status: RewardStatus = "pending";
    let claimableData: ClaimablesData | undefined;

    // Check if all results are claimed
    const allClaimed = results.every((r) => r.status === "claimed");
    if (allClaimed) {
      status = "claimed";
    } else {
      // Check if any result is claimable
      const anyClaimable = results.some((r) => r.status === "claimable");
      if (anyClaimable) {
        status = "claimable";
        // Find the claimableData from any claimable result (they should all have the same claimableData for the epoch)
        claimableData = results.find((r) => r.claimableData)?.claimableData;
      }
    }

    // Use cooldownEnd from any result (they should all have the same cooldownEnd for the epoch)
    const cooldownEnd = results.find((r) => r.cooldownEnd)?.cooldownEnd;

    epochGroups.push({
      epochId,
      results,
      status,
      cooldownEnd,
      claimableData,
      totalRewards,
    });
  }

  // Sort by epochId descending (newest first)
  return epochGroups.sort((a, b) => b.epochId - a.epochId);
}
